import { useEffect, useState } from "react";
import { AlertTriangle, Check } from "lucide-react";

import { useTranslation } from "@/i18n";
import { cn } from "@/lib/utils";

interface JsonEditorProps {
  /** Stringified JSON. The parent owns the raw text so undo +
   * un-parseable in-progress edits don't bounce the cursor. */
  value: string;
  onChange: (value: string) => void;
  /** Called with the parsed JSON when the input is valid, or with
   * ``null`` when the input is empty / invalid. The parent can
   * use this to decide whether to enable a submit button. */
  onValidChange?: (parsed: unknown | null) => void;
  rows?: number;
  placeholder?: string;
  className?: string;
}

/**
 * Lightweight JSON textarea + client-side ``JSON.parse``
 * validation. No external dependency — just a monospace textarea
 * with a status line. Suitable for the Policy rule_definition
 * editor where we want to expose the DSL without dragging in a
 * full code editor (Monaco / CodeMirror).
 */
export function JsonEditor({
  value,
  onChange,
  onValidChange,
  rows = 12,
  placeholder,
  className,
}: JsonEditorProps) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [parsed, setParsed] = useState<unknown | null>(null);

  useEffect(() => {
    if (!value.trim()) {
      setError(null);
      setParsed(null);
      onValidChange?.(null);
      return;
    }
    try {
      const next = JSON.parse(value);
      setError(null);
      setParsed(next);
      onValidChange?.(next);
    } catch (e) {
      const message =
        e instanceof Error ? e.message : t("shared.jsonEditor.invalid");
      setError(message);
      setParsed(null);
      onValidChange?.(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const isValid = error === null && parsed !== null;

  const handleFormat = () => {
    if (!isValid) return;
    try {
      onChange(JSON.stringify(parsed, null, 2));
    } catch {
      // Should never happen — we just parsed it.
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder ?? "{}"}
        spellCheck={false}
        className={cn(
          "block w-full rounded-md border bg-white px-3 py-2 font-mono text-xs leading-relaxed text-ocean-900 transition-colors focus:outline-none focus:ring-2 focus:ring-ocean-400",
          error ? "border-danger-600/40" : "border-ocean-100"
        )}
      />
      <div className="flex items-center justify-between gap-2 text-xs">
        <div
          className={cn(
            "inline-flex items-center gap-1.5",
            error
              ? "text-danger-600"
              : isValid
                ? "text-success-600"
                : "text-neutral-500"
          )}
        >
          {error ? (
            <>
              <AlertTriangle size={12} />
              {error}
            </>
          ) : isValid ? (
            <>
              <Check size={12} />
              {t("shared.jsonEditor.valid")}
            </>
          ) : (
            <span>{t("shared.jsonEditor.prompt")}</span>
          )}
        </div>
        <button
          type="button"
          onClick={handleFormat}
          disabled={!isValid}
          className="text-ocean-600 transition-colors hover:text-ocean-800 disabled:cursor-not-allowed disabled:text-neutral-300"
        >
          {t("shared.jsonEditor.format")}
        </button>
      </div>
    </div>
  );
}
